/**
 * Glow — purely decorative atom: a soft, blurred ambient halo that
 * highlights a region of a surface. Driven by token roles
 * (`primary` / `good` / `bad` / `alert`) so the tint stays consistent
 * with the rest of the design system.
 *
 * Anchored absolutely inside its containing block. The container must
 * provide its own `position: relative` (or any positioned context)
 * and — when the glow is meant to be clipped to the surface — its own
 * `overflow: hidden`. The glow itself never traps clicks (`pointer-
 * events: none`) so layered controls keep working.
 *
 * Figma source: the green halo under the "Войти" button on
 * `Signing In / Phone / Fill` (`8764:22692`) — a vector with
 * `good/good` fill, layer blur, and bottom-center alignment inside
 * the form card. This atom abstracts that pattern so other surfaces
 * (Sign Up, PasswordRecovery, future flows) can reuse it without
 * redrawing the vector.
 *
 * Conventional usage:
 *   ```tsx
 *   <FormCard>
 *     {isValid && <Glow color="good" />}
 *     <FormContent>...</FormContent>
 *   </FormCard>
 *   ```
 *
 * The `FormContent` wrapper must carry its own `position: relative`
 * + `zIndex` so it stays above the Glow inside the same stacking
 * context. Glow itself takes the lowest z-index inside the container.
 *
 * @format
 */

import type {CSSProperties} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTheme, type Theme} from '@mui/material/styles';

import {
  useSurfaceContext,
  type SurfaceContext as SurfaceContextValue,
} from 'ui/theme/surfaceContext';

export const GLOW_COLORS = ['primary', 'good', 'bad', 'alert'] as const;
export type GlowColor = (typeof GLOW_COLORS)[number];

export const GLOW_POSITIONS = [
  'top-left',
  'top-center',
  'top-right',
  'center-left',
  'center',
  'center-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const;
export type GlowPosition = (typeof GLOW_POSITIONS)[number];

export const GLOW_SIZES = ['s', 'm', 'l'] as const;
export type GlowSize = (typeof GLOW_SIZES)[number];

export interface GlowProps {
  /** Token role driving the halo tint. */
  color: GlowColor;

  /** Anchor inside the containing block. Default `bottom-center`. */
  position?: GlowPosition;

  /**
   * Base size step. Each step picks both the rectangle dimensions
   * and the default blur radius:
   *   - `s` — 300 × 80, blur 50
   *   - `m` — 590 × 150, blur 75 (Figma source)
   *   - `l` — 880 × 220, blur 100
   * Default `m`.
   */
  size?: GlowSize;

  /** Override blur radius (px). Defaults follow `size`. */
  blur?: number;

  /** Opacity 0..1. Default `0.5` (matches Figma layer-blur opacity). */
  opacity?: number;

  /**
   * How many pixels of the glow's edge sit INSIDE the parent when an
   * **edge anchor** is used (`top-*` / `bottom-*` / `*-left` /
   * `*-right`). The rest of the glow hangs outside the parent — with
   * `overflow: hidden` on the parent only the overlap portion is
   * visible, which gives the «halo bleeds through the edge» look.
   *
   * Default `48` (taken from the Figma source on the auth screens).
   * Set to the glow's full size to keep it entirely inside; set to
   * `0` to anchor the glow's edge exactly at the parent's edge with
   * the body fully outside (a "torch behind the wall" look).
   *
   * Ignored on center anchors (`center`, `center-left`,
   * `center-right`) where there's no specific edge to overlap with.
   */
  overlap?: number;

  /**
   * Surface-context override. Default is inherited from the enclosing
   * `<SurfaceContextProvider>`. Brand roles are consistent across
   * contexts in our token system, so this only matters if we add a
   * non-brand tint role later.
   */
  context?: SurfaceContextValue;
}

const SIZE_DEFAULTS: Record<GlowSize, {width: number; height: number; blur: number}> = {
  s: {width: 300, height: 80, blur: 50},
  m: {width: 590, height: 150, blur: 75},
  l: {width: 880, height: 220, blur: 100},
};

/**
 * Build the position style for a given anchor.
 *
 * Edge anchors place the glow OUTSIDE the parent, with `overlap` px
 * of the glow's edge inside the parent. The "negative inset"
 * arithmetic uses `width - overlap` / `height - overlap` so the glow
 * is shifted out exactly enough to leave the overlap visible inside.
 *
 * Center anchors ignore overlap — they place the glow centered on
 * the axis with no protrusion.
 */
function computePositionStyle(
  position: GlowPosition,
  width: number,
  height: number,
  overlap: number,
): CSSProperties {
  // `bleed` is how many px of the glow stick out beyond the parent edge.
  // The opposite side carries `overlap` px inside the parent.
  const bleedV = -(height - overlap);
  const bleedH = -(width - overlap);

  const centerY = 'translateY(-50%)';
  const centerX = 'translateX(-50%)';

  switch (position) {
    case 'top-left':
      return {top: bleedV, left: bleedH};
    case 'top-center':
      return {top: bleedV, left: '50%', transform: centerX};
    case 'top-right':
      return {top: bleedV, right: bleedH};
    case 'center-left':
      return {top: '50%', left: 0, transform: centerY};
    case 'center':
      return {top: '50%', left: '50%', transform: 'translate(-50%, -50%)'};
    case 'center-right':
      return {top: '50%', right: 0, transform: centerY};
    case 'bottom-left':
      return {bottom: bleedV, left: bleedH};
    case 'bottom-center':
      return {bottom: bleedV, left: '50%', transform: centerX};
    case 'bottom-right':
      return {bottom: bleedV, right: bleedH};
  }
}

function resolveColor(theme: Theme, role: GlowColor): string {
  // No inverse-* switching: SurfaceContextProvider / Glow's own root
  // sets `data-color-scheme` and MUI's cascade resolves these to
  // the right per-context values. Brand mains are intentionally
  // identical across contexts (Figma rule), so visually only `container`
  // tints would differ — but Glow renders `main` per Figma source.
  const p = theme.palette;
  switch (role) {
    case 'primary':
      return p.primary.main;
    case 'good':
      return p.good.main;
    case 'bad':
      return p.bad.main;
    case 'alert':
      return p.alert.main;
    default: {
      const _exhaustive: never = role;
      throw new Error(`unhandled case: ${String(_exhaustive)}`);
    }
  }
}

const GlowElement = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $color: string;
  $width: number;
  $height: number;
  $blur: number;
  $opacity: number;
  $position: GlowPosition;
  $overlap: number;
}>(({$color, $width, $height, $blur, $opacity, $position, $overlap}) => ({
  position: 'absolute',
  // No z-index here — glow renders BEHIND siblings that carry their own
  // positioning + z-index (typical FormContent wrapper). Inside a stacking
  // context where every other child sits at z-index 1+, the glow's default
  // 'auto' resolves to the lowest in DOM order.
  width: `${$width}px`,
  height: `${$height}px`,
  backgroundColor: $color,
  filter: `blur(${$blur}px)`,
  opacity: $opacity,
  pointerEvents: 'none',
  ...(computePositionStyle($position, $width, $height, $overlap) as object),
}));

export function Glow({
  color,
  position = 'bottom-center',
  size = 'm',
  blur,
  opacity = 0.5,
  overlap = 48,
  context: contextProp,
}: GlowProps) {
  const context = useSurfaceContext(contextProp);
  const theme = useTheme();
  const fillColor = resolveColor(theme, color);
  const defaults = SIZE_DEFAULTS[size];

  return (
    <GlowElement
      aria-hidden='true'
      data-color-scheme={context}
      $color={fillColor}
      $width={defaults.width}
      $height={defaults.height}
      $blur={blur ?? defaults.blur}
      $opacity={opacity}
      $position={position}
      $overlap={overlap}
    />
  );
}
